import { useCallback, useState } from 'react'
import { t } from '@lingui/core/macro'
import { formatISO, parseISO } from 'date-fns'
import type { OptionSelect } from '@/core/utils/types'
import type { CargoUsuarioForm } from '../types/cargo'
import { usePodeAdicionarCargo } from './use-pode-adicionar-cargo'
import { useCargos } from './use-cargos'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { ICargosUsuarioTable } from '../components/UsuarioFirstPage/cargos-usuario-table'

type UseCargosUsuarioTable = Pick<
  ICargosUsuarioTable,
  'values' | 'dealer' | 'append' | 'remove' | 'showDepartamentos'
>

export const useCargosUsuarioTable = ({
  values,
  dealer,
  append,
  remove,
  showDepartamentos
}: UseCargosUsuarioTable) => {
  const cargosUsuario = values.cargos as CargoUsuarioForm[]
  const [date, setDate] = useState<Date | undefined>(undefined)
  const [cargo, setCargo] = useState<OptionSelect | undefined>(undefined)
  const [departamento, setDepartamento] = useState<OptionSelect | undefined>(
    undefined
  )
  const { snackbar } = useSnackbar()
  const { validate, loading: validating } = usePodeAdicionarCargo()

  const selectedIds = cargosUsuario
    .filter(cargo => cargo.dealerId === dealer.value)
    .map(c => c.value)

  const { cargosOptions } = useCargos(selectedIds)

  const cargosUsuarioByDealer = cargosUsuario
    .filter(item => item.dealerId === dealer.value)
    .sort(
      (a, b) =>
        parseISO(a.dataInicio).getTime() - parseISO(b.dataInicio).getTime()
    )

  const validateCampos = useCallback(
    (hasCargo: boolean, hasDepartamento: boolean, hasDate: boolean) => {
      if (showDepartamentos && (!hasCargo || !hasDepartamento || !hasDate)) {
        snackbar.show(
          'error',
          t`Cargo, Departamento e Data de inicio são campos obrigatórios`
        )
        throw Error(
          t`Cargo, Departamento e Data de inicio são campos obrigatórios`
        )
      }

      if (!showDepartamentos && (!hasCargo || !hasDate)) {
        snackbar.show(
          'error',
          t`Cargo e Data de inicio são campos obrigatórios`
        )
        throw Error(t`Cargo e Data de inicio são campos obrigatórios`)
      }
    },
    [showDepartamentos, snackbar]
  )

  const validateCargoDuplicado = useCallback(
    (cargoId?: string) => {
      const hasCargo = cargosUsuario
        .filter((it: CargoUsuarioForm) => it.dealerId === dealer.value)
        .some((it: CargoUsuarioForm) => it.value === cargoId)

      if (hasCargo) {
        snackbar.show('error', t`O cargo selecionado já foi atribuído!`)
        throw Error(t`O cargo selecionado já foi atribuído!`)
      }
    },
    [dealer, cargosUsuario, snackbar]
  )

  const validateDatasCargosAdicionados = useCallback(
    async (cargoId: string, dataInicio: string, dealerId?: string) => {
      const cargoAdicionado = {
        cargoId,
        dealerId,
        dataInicio
      }

      const existing = cargosUsuario.map(i => ({
        cargoId: i.value,
        dataInicio: i.dataInicio,
        dealerId: i.dealerId
      }))

      const ok = await validate(cargoAdicionado, existing)
      if (!ok) throw Error('Não foi possível adicionar o cargo')
    },
    [cargosUsuario, validate]
  )

  const handleInsert = useCallback(
    async (
      _dealer: OptionSelect,
      _cargo?: OptionSelect,
      _departamento?: OptionSelect,
      _date?: Date
    ) => {
      try {
        validateCampos(!!cargo?.value, !!departamento?.value, !!date)
        validateCargoDuplicado(cargo?.value)

        const novoCargo: CargoUsuarioForm = {
          label: _cargo?.label ?? '',
          value: _cargo?.value ?? '',
          dataInicio: formatISO(_date as Date, {
            representation: 'date'
          }),
          dealerId: _dealer.value,
          dealerNome: _dealer.label,
          departamentoId: _departamento?.value,
          departamentoNome: _departamento?.label
        }

        await validateDatasCargosAdicionados(
          novoCargo.value,
          novoCargo.dataInicio,
          novoCargo.dealerId
        )

        append(novoCargo)

        setDate(undefined)
        setCargo(undefined)
        setDepartamento(undefined)
      } catch (e) {
        console.error(e)
      }
    },
    [
      cargo,
      date,
      departamento,
      append,
      validateCampos,
      validateCargoDuplicado,
      validateDatasCargosAdicionados
    ]
  )

  const handleRemove = (cargoId: string, dealerId?: string) => {
    let cargoIndexOnValues = -1
    if (!dealerId) {
      cargoIndexOnValues = cargosUsuario.findIndex(it => it.value === cargoId)
    } else {
      cargoIndexOnValues = cargosUsuario.findIndex(
        it => it.value === cargoId && it.dealerId === dealerId
      )
    }
    if (cargoIndexOnValues !== -1) {
      remove(cargoIndexOnValues)
    } else {
      snackbar.show('error', t`Falha ao remover cargo!`)
    }
  }

  return {
    cargosOptions,
    setCargo,
    cargo,
    setDepartamento,
    departamento,
    handleInsert,
    validating,
    cargosUsuarioByDealer,
    handleRemove,
    date,
    setDate
  }
}
